// client/src/components/pages/LexiStar/FleetTargets/fleetLogic.ts (ST-00247)
//
// What the availability and utilisation visuals share, pure: the five states a truck's time is
// split into (words, definitions, colours from the shared asset status palette), the two
// percentages' formulas, formatting, and the rows and series the charts draw. The figures are
// the server's (GET /api/lexistar/fleet-availability, performance/fleetTime.ts over the
// ST-00249 time model): this module never re-derives them, so the charts, the table and the
// targets agree.

import {
  ASSET_STATUS_CLASSES,
  assetStatusVar,
  type AssetStatusKey,
} from '../../../map/assetStatus';
import { periodTick } from '../Dashboard/periodLogic';
import type {
  FleetAssetAvailability,
  FleetAvailabilityResponse,
  FleetBucket,
  FleetGrain,
  FleetState,
} from '../../../../types/lexistar-performance';

// ── The states ──────────────────────────────────────────────────────────────────────────

export interface StateMeta {
  label: string;
  /** What counts as this state, in a sentence (the definitions panel, the help article). */
  definition: string;
  /** The shared asset status key whose colours it wears. */
  statusKey: AssetStatusKey;
  /** CSS colour of the solid (theme-aware custom property). */
  solid: string;
  /** Tailwind class of a swatch / segment. */
  dot: string;
}

const meta = (label: string, definition: string, statusKey: AssetStatusKey): StateMeta => ({
  label,
  definition,
  statusKey,
  solid: assetStatusVar(statusKey, 'solid'),
  dot: ASSET_STATUS_CLASSES[statusKey].dot,
});

/**
 * The colours are the shared asset status palette (map/assetStatus): Utilised is "Travelling
 * full", Idle "Parked", Delay, Planned maintenance and Breakdown the maintenance module's own —
 * the same colours as the Maintenance timeline, the control room and the Status Board.
 */
export const STATE_META: Record<FleetState, StateMeta> = {
  utilised: meta(
    'Utilised',
    'On one of your organisation’s own trips: from dispatch to completion. A trip still under way counts up to now.',
    'loaded'
  ),
  idle: meta('Idle', 'Available, but on no trip: nothing booked against the truck and no trip running.', 'parked'),
  delay: meta(
    'Delay',
    'A delay logged on the Maintenance page: the truck could work but is held up — a queue, no driver, the weather.',
    'delay'
  ),
  planned_maintenance: meta(
    'Down for maintenance',
    'Booked into the workshop on the Maintenance page: a service, tyres, an inspection, a scheduled repair.',
    'planned_maintenance'
  ),
  breakdown: meta(
    'Breakdown',
    'Reported broken down on the Maintenance page, until the breakdown is closed. Waiting for parts counts here.',
    'breakdown'
  ),
};

/** Stack and legend order: the working time first, then what took it away (weakest to strongest). */
export const STATE_ORDER: FleetState[] = ['utilised', 'idle', 'delay', 'planned_maintenance', 'breakdown'];

export const SECONDS_FIELD: Record<FleetState, keyof Pick<FleetBucket, 'utilised_s' | 'idle_s' | 'delay_s' | 'planned_maintenance_s' | 'breakdown_s'>> = {
  utilised: 'utilised_s',
  idle: 'idle_s',
  delay: 'delay_s',
  planned_maintenance: 'planned_maintenance_s',
  breakdown: 'breakdown_s',
};

export const AVAILABLE_DEFINITION = 'Available = Utilised + Idle: no maintenance, breakdown or delay covers the time.';

export const FORMULAS = {
  availability: 'Availability = (Utilised + Idle) ÷ all the time counted',
  utilisation: 'Utilisation = Utilised ÷ (Utilised + Idle) — how much of the available time was used',
  precedence:
    'When two overlap, the stronger one counts: Breakdown, then Down for maintenance, then Delay, then Utilised. A delay during a trip is Delay.',
  counted: 'Only time that has passed counts: this week or month so far, never what is booked ahead.',
} as const;

/** The two series of the per-truck bars and the trend: Availability (brand teal) and Utilisation (the Utilised blue). */
export const SERIES = {
  availability: { label: 'Availability', color: 'rgb(var(--tw-primary-600))', dot: 'bg-primary-600' },
  utilisation: { label: 'Utilisation', color: assetStatusVar('loaded', 'solid'), dot: ASSET_STATUS_CLASSES.loaded.dot },
} as const;

// ── Formatting ──────────────────────────────────────────────────────────────────────────

const group = (n: number, digits: number): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** "83.3%" / "—". */
export const fmtPct = (v: number | null | undefined): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : `${group(v, 1)}%`;

/** Seconds as hours: "12.5 h", "312 h", "0 h". */
export function fmtHours(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const h = seconds / 3600;
  if (h === 0) return '0 h';
  return `${group(h, Math.abs(h) < 100 ? 1 : 0)} h`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A period as a reader names it: "19 Oct", "w/c 12 Oct", "Oct 2026"; "(so far)" while it runs. */
export function bucketLabel(b: Pick<FleetBucket, 'start' | 'partial'>, grain: FleetGrain): string {
  const [y, m, d] = b.start.split('-').map(Number) as [number, number, number];
  const base = grain === 'month' ? `${MONTHS[m - 1]} ${y}` : grain === 'week' ? `w/c ${d} ${MONTHS[m - 1]}` : `${d} ${MONTHS[m - 1]}`;
  return b.partial ? `${base} (so far)` : base;
}

/** The axis tick of a period: "19 Oct" / "Oct 26". */
export const bucketTick = (start: string, grain: FleetGrain): string => periodTick(start, grain);

/** A truck's name: registration, else fleet number. */
export const truckLabel = (a: Pick<FleetAssetAvailability, 'registration' | 'fleet_no' | 'asset_id'>): string =>
  a.registration || a.fleet_no || `Truck ${a.asset_id.slice(0, 8)}`;

// ── Rows and series ─────────────────────────────────────────────────────────────────────

export type TruckSort = 'truck' | 'availability' | 'utilisation';

export interface TruckRow {
  id: string;
  label: string;
  fleetNo: string | null;
  availability: number | null;
  utilisation: number | null;
  /** Seconds per state. */
  seconds: Record<FleetState, number>;
  /** Share of the counted time per state, 0–1 (the segmented bar). */
  share: Record<FleetState, number>;
  calendarS: number;
  trips: number;
}

export function rowOf(a: FleetAssetAvailability): TruckRow {
  const t = a.totals;
  const seconds = Object.fromEntries(STATE_ORDER.map((s) => [s, t[SECONDS_FIELD[s]]])) as Record<FleetState, number>;
  const share = Object.fromEntries(STATE_ORDER.map((s) => [s, t.calendar_s > 0 ? seconds[s] / t.calendar_s : 0])) as Record<FleetState, number>;
  return {
    id: a.asset_id,
    label: truckLabel(a),
    fleetNo: a.fleet_no,
    availability: t.availability_pct,
    utilisation: t.utilisation_pct,
    seconds,
    share,
    calendarS: t.calendar_s,
    trips: a.trips,
  };
}

/** Lowest first for a percentage (the trucks to look at come first); a truck without a figure last. */
export function sortRows(rows: TruckRow[], sort: TruckSort): TruckRow[] {
  const byLabel = (a: TruckRow, b: TruckRow) => a.label.localeCompare(b.label, 'en', { numeric: true });
  if (sort === 'truck') return [...rows].sort(byLabel);
  const v = (r: TruckRow) => (sort === 'availability' ? r.availability : r.utilisation);
  return [...rows].sort((a, b) => {
    const x = v(a);
    const y = v(b);
    if (x === null && y === null) return byLabel(a, b);
    if (x === null) return 1;
    if (y === null) return -1;
    return x - y || byLabel(a, b);
  });
}

export const isTruckSort = (v: unknown): v is TruckSort => v === 'truck' || v === 'availability' || v === 'utilisation';

export function truckRows(res: FleetAvailabilityResponse | null, sort: TruckSort = 'truck'): TruckRow[] {
  return res ? sortRows(res.assets.map(rowOf), sort) : [];
}

export interface TrendPoint {
  start: string;
  tick: string;
  label: string;
  availability: number | null;
  utilisation: number | null;
  partial: boolean;
}

/** The trend of the fleet or one truck, one point per period. */
export function trendOf(buckets: FleetBucket[], grain: FleetGrain): TrendPoint[] {
  return buckets.map((b) => ({
    start: b.start,
    tick: bucketTick(b.start, grain),
    label: bucketLabel(b, grain),
    availability: b.availability_pct,
    utilisation: b.utilisation_pct,
    partial: b.partial,
  }));
}

/** The chart's gist in words (screen readers, the chart's caption). */
export function fleetSummary(res: FleetAvailabilityResponse | null): string {
  if (!res) return '';
  const n = res.assets.length;
  if (!n) return 'No trucks are registered for your organisation.';
  const t = res.fleet.totals;
  const lowest = sortRows(res.assets.map(rowOf), 'availability')[0];
  const low = lowest && lowest.availability !== null && n > 1 ? ` Lowest availability: ${lowest.label} at ${fmtPct(lowest.availability)}.` : '';
  return `${n} truck${n === 1 ? '' : 's'}: fleet availability ${fmtPct(t.availability_pct)}, utilisation ${fmtPct(t.utilisation_pct)} (${fmtHours(t.utilised_s)} utilised of ${fmtHours(t.available_s)} available).${low}`;
}

/** Hours of every state over the fleet, for the stat tiles. */
export function fleetTiles(res: FleetAvailabilityResponse | null): Array<{ id: string; label: string; value: string; hint: string }> {
  if (!res) return [];
  const t = res.fleet.totals;
  return [
    { id: 'availability', label: 'Availability', value: fmtPct(t.availability_pct), hint: `${fmtHours(t.available_s)} of ${fmtHours(t.calendar_s)}` },
    { id: 'utilisation', label: 'Utilisation', value: fmtPct(t.utilisation_pct), hint: `${fmtHours(t.utilised_s)} on trips` },
    { id: 'idle', label: 'Idle', value: fmtHours(t.idle_s), hint: 'Available, on no trip' },
    { id: 'delay', label: 'Delays', value: fmtHours(t.delay_s), hint: 'Logged on the Maintenance page' },
    {
      id: 'down',
      label: 'Maintenance and breakdowns',
      value: fmtHours(t.planned_maintenance_s + t.breakdown_s),
      hint: `${fmtHours(t.breakdown_s)} broken down`,
    },
  ];
}
